"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar, Plus } from "lucide-react";
import { toast } from "react-hot-toast";
import moment, { defaultDateRange, startOfDay, endOfDay, isDateTimeInRange, formatQueryDateRange, parseAppDateTime, formatInAppTz } from "../utils/timezone";
import { bartenderTipoutService } from "../apis/bartenderTipoutService";
import { employeeManagementService } from '../apis/employeeManagement';
import DateRangePicker from "../components/ui/date-range-picker";
import { useAuth } from "../context/AuthContext";
import { BartenderTipAddModal } from "../components/bartender-tip-add-modal";
import { BartenderTipEditModal } from "../components/bartender-tip-edit-modal";
import { BartenderTipDeleteModal } from "../components/bartender-tip-delete-modal";
import { BartenderTipsTransactionTable } from "../components/bartender-tips-transaction-table";

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EmployeeTotalsTable } from "../components/employee-total";
import { base64Logo } from '../constants/pdfLogo'

export default function BartenderTipsRecords() {
    const { currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [allemployees, setallemployees] = useState([]);
    const [refreshFlag, setRefreshFlag] = useState(false);
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);

    // Filter states
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await employeeManagementService.getAllEmployees({ limit: 1000000 });
                if (response.success) {
                    setallemployees(response?.data?.body?.data?.employees);
                }
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        };

        fetchEmployees();
    }, []);

    // Fetch records with date filtering
    const fetchRecords = useCallback(async (startDate = null, endDate = null) => {
        try {
            setIsLoading(true);
            const params = {};
            if (startDate && endDate) {
                Object.assign(params, formatQueryDateRange(startDate, endDate));
            }

            const response = await bartenderTipoutService.getTipHistory(params);

            if (response.success && Array.isArray(response.data)) {
                setRecords(response.data);
            } else {
                console.error("Invalid records response:", response);
                setRecords([]);
            }
        } catch (err) {
            console.error("Error fetching records:", err);
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial data fetch - moved this function definition before the useEffect that uses it
    const fetchInitialData = useCallback(async () => {
        try {
            setIsLoading(true);
            await fetchRecords(dateRange.start, dateRange.end);
        } catch (err) {
            console.error("Error fetching initial data:", err);
            setError("Failed to load data");
            toast.error("Failed to load floor tips data");
        } finally {
            setIsLoading(false);
        }
    }, [fetchRecords, dateRange.start, dateRange.end]);

    useEffect(() => {
        fetchRecords(dateRange.start, dateRange.end);
    }, [dateRange, fetchRecords, refreshFlag]);

    // Initial data fetch
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Handle adding a new record
    const handleAddRecord = async ({ empname, amount, owetotoast, shift }) => {
        try {
            setIsLoading(true);
            const response = await bartenderTipoutService.addRecord({
                empname,
                amountcash: Number(amount), // Convert to number
                owetotoast: Number(owetotoast) || 0, // Convert to number
                added_by: currentUser.username,
                bar_back: false, // Use boolean directly
                shift,
            });

            if (response.success) {
                toast.success("Record added successfully");
                // Refresh data after adding
                await fetchRecords(dateRange.start, dateRange.end);
                setRefreshFlag(prev => !prev);
                setShowAddModal(false);

            } else {
                toast.error(response.error || "Failed to add record");
            }
        } catch (err) {
            console.error("Error adding record:", err);
            toast.error("An error occurred while adding record");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle record update
    const handleUpdateRecord = async (data) => {
        try {
            setIsLoading(true);
            const response = await bartenderTipoutService.updateRecord(data.id, {
                date: parseAppDateTime(data.date)?.format("YYYY-MM-DDTHH:mm:ss") ?? data.date,
                empname: data.empname,
                amountcash: parseFloat(data.amount) || 0,
                owetotoast: parseFloat(data.owetotoast) || 0,
                bar_back: data.bar_back || 0,
                added_by: data.added_by,
                previousAmount: data.previousAmount
            });

            if (response.success) {
                toast.success("Record updated successfully");
                // Refresh data after updating
                await fetchRecords(dateRange.start, dateRange.end);
                setRefreshFlag(prev => !prev);
                setShowEditModal(false);

            } else {
                toast.error(response.error || "Failed to update record");
            }
        } catch (err) {
            console.error("Error updating record:", err);
            toast.error("An error occurred while updating record");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle record deletion
    const handleDeleteRecord = async () => {
        if (!currentRecord) return false;

        try {
            setIsLoading(true);
            const response = await bartenderTipoutService.deleteRecord(currentRecord.id);

            if (response.success) {
                toast.success("Record deleted successfully");
                // Refresh data after deletion
                await fetchRecords(dateRange.start, dateRange.end);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                toast.error(response.error || "Failed to delete record");
                return false;
            }
        } catch (err) {
            console.error("Error deleting record:", err);
            toast.error("An error occurred while deleting record");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // Filter records based on date range and search term
    const filteredRecords = useMemo(() => {
        if (!Array.isArray(records)) return [];

        return records.filter(record => {
            const inDateRange = isDateTimeInRange(record.date, dateRange.start, dateRange.end);

            const matchesSearch =
                !searchTerm ||
                (record.employee && record.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (record.added_by && record.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

            return inDateRange && matchesSearch;
        });
    }, [records, dateRange, searchTerm]);

    const employeeTotals = useMemo(() => {
        const totalsMap = {};

        filteredRecords.forEach(record => {
            const employee = record.employee;

            if (!totalsMap[employee]) {
                totalsMap[employee] = {
                    employee,
                    tipsReceived: 0,
                    tax: 0
                };
            }

            totalsMap[employee].tipsReceived += parseFloat(record.tip_out_cash || 0);
            totalsMap[employee].tax += parseFloat(record.tax || 0);
        });

        return Object.values(totalsMap);
    }, [filteredRecords]);
    // Format date range for display
    const formatDateRange = () => {
        return `${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")}`;
    };

    // Calculate period summary
    const periodSummary = useMemo(() => {
        if (!Array.isArray(filteredRecords)) {
            return { tipsReceived: 0, tax: 0 };
        }

        return filteredRecords.reduce(
            (acc, record) => {
                return {
                    tipsReceived: acc.tipsReceived + parseFloat(record.tip_out_cash || 0),
                    tax: acc.tax + parseFloat(record.tax || 0)
                };
            },
            { tipsReceived: 0, tax: 0 }
        );
    }, [filteredRecords]);

    // Handle edit record
    const handleEditRecord = (record) => {
        setCurrentRecord(record);
        setShowEditModal(true);
    };

    const handleDeleteRecordClick = (record) => {
        setCurrentRecord(record);
        setShowDeleteModal(true);
    };

    const formatCurrency = (value) => {
        return parseFloat(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="text-red-500 text-xl">{error}</div>
            </div>
        );
    }


    const exportBartenderTipsRecordsToPDF = () => {
        try {
            const doc = new jsPDF();

            // Set document properties
            doc.setProperties({
                title: 'Floor Tips Report',
                subject: 'Floor Tips Transactions',
                author: 'Elite Poker Lounge',
                creator: 'Floor Tips System'
            });

            // Add logo (if you have base64Logo available)
            if (typeof base64Logo !== 'undefined') {
                const logoWidth = 40;
                const logoHeight = 20;
                doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight);
            }

            // Add timestamp
            const now = new Date();
            const dateTime = now.toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Date & Time: ${dateTime}`, 14, 15);
            doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

            // Title
            doc.setFontSize(18);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Floor Tips Records', 14, 40);

            // Summary section
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Balance Summary:', 14, 50);

            doc.setFont(undefined, 'normal');
            // Calculate tip pool from filtered records
            const calculatedTipPool = filteredRecords.reduce(
                (sum, record) => sum + parseFloat(record.tip_out_cash || 0),
                0
            );
            doc.text(`Tip Pool: $${formatCurrency(calculatedTipPool)}`, 14, 60);
            doc.text(`Total Added Tokes: $${formatCurrency(calculatedTokes)}`, 14, 70);
            doc.text(`Total payout: $${formatCurrency(calculatedPaidOut)}`, 14, 80);

            // Period summary section
            doc.setFont(undefined, 'bold');
            doc.text('Period Summary:', 14, 95);

            doc.setFont(undefined, 'normal');
            doc.text(`Total Tips Received: $${formatCurrency(periodSummary.tipsReceived)}`, 14, 105);
            doc.text(`Total Tax: $${formatCurrency(periodSummary.tax)}`, 14, 115);

            // Employee Totals Section
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Employee Totals:', 14, 130);

            // Employee totals table
            const employeeHeaders = ['Employee', 'Tips Received', 'Tax', 'Paid Out'];
            const employeeData = employeeTotals.map(employee => [
                employee.employee,
                `$${formatCurrency(employee.tipsReceived)}`,
                `$${formatCurrency(employee.tax)}`,
                `$${formatCurrency(employee.tipsReceived - employee.tax)}`
            ]);

            // Add employee totals row
            employeeData.push([
                'TOTAL',
                `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tipsReceived, 0))}`,
                `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tax, 0))}`,
                `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + (emp.tipsReceived - emp.tax), 0))}`
            ]);

            let currentY = 135;

            if (employeeTotals.length > 0) {
                autoTable(doc, {
                    head: [employeeHeaders],
                    body: employeeData,
                    startY: currentY,
                    theme: 'plain',
                    styles: {
                        fontSize: 9,
                        cellPadding: 3,
                        textColor: [0, 0, 0],
                        lineColor: [0, 0, 0],
                        lineWidth: 0.1
                    },
                    headStyles: {
                        fillColor: false,
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    bodyStyles: {
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { cellWidth: 60 },
                        1: { cellWidth: 50, halign: 'right' },
                        2: { cellWidth: 50, halign: 'right' }
                    },
                    margin: { left: 14, right: 14 },
                    tableLineColor: [0, 0, 0],
                    tableLineWidth: 0.1,
                    didParseCell: function (data) {
                        // Make the total row bold
                        if (data.row.index === employeeData.length - 1) {
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                });

                currentY = doc.lastAutoTable.finalY + 15;
            }

            // Transaction Details Section
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Transaction Details:', 14, currentY);

            // Prepare transaction table data
            const transactionHeaders = [
                'Date',
                'Employee',
                'Tip Out Cash',
                'Tax',
                'Paid Out',
                'Added By'
            ];

            const transactionData = filteredRecords.map(record => [
                parseAppDateTime(record.date)?.format("MM/DD/YYYY hh:mm A") ?? formatInAppTz(record.date),
                record.employee || 'N/A',
                `$${formatCurrency(record.tip_out_cash)}`,
                `$${formatCurrency(record.tax)}`,
                `$${formatCurrency(parseFloat(record.tip_out_cash || 0) - parseFloat(record.tax || 0))}`,
                record.added_by || 'N/A'
            ]);

            // Add transaction details table
            autoTable(doc, {
                head: [transactionHeaders],
                body: transactionData,
                startY: currentY + 5,
                theme: 'plain',
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1
                },
                headStyles: {
                    fillColor: false,
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    lineWidth: 0.1,
                    lineColor: [0, 0, 0]
                },
                columnStyles: {
                    0: { cellWidth: 35 },
                    1: { cellWidth: 30 },
                    2: { cellWidth: 25, halign: 'right' },
                    3: { cellWidth: 25, halign: 'right' },
                    4: { cellWidth: 20, halign: 'center' },
                    5: { cellWidth: 30 }
                },
                margin: { left: 14, right: 14 },
                tableLineColor: [0, 0, 0],
                tableLineWidth: 0.1
            });

            // Save the PDF
            const fileName = `Floor_Tips_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
            doc.save(fileName);

            toast.success('PDF exported successfully!');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF. Please try again.');
        }
    };

    const calculatedTipPool = filteredRecords.reduce(
        (sum, record) => sum + parseFloat(record.tip_out_cash || 0),
        0
    );

    const calculatedTokes = filteredRecords.reduce(
        (sum, record) => sum + parseFloat(record.tax || 0),
        0
    );
    const calculatedPaidOut = filteredRecords.reduce(
        (sum, record) => sum + (parseFloat(record.tip_out_cash || 0) - parseFloat(record.tax || 0)),
        0
    );


    return (
        <div className="bg-gray-50 p-6 flex flex-col">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800">Floor Tips Records</h1>
                <p className="text-gray-600 mt-2">Manage and track floor tip distributions</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                            Tip Pool
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">
                                ${formatCurrency(calculatedTipPool)}
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                            Total Added Tokes
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">
                                ${formatCurrency(calculatedTokes)}
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                            Total payout
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                                ${formatCurrency(calculatedPaidOut)}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>


            {/* Add Record Button */}
            <div className="flex justify-end mb-6">
                <Button
                    onClick={() => setShowAddModal(true)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    disabled={isLoading}
                >
                    <Plus className="mr-2 h-4 w-4" /> Add Record
                </Button>
            </div>

            {/* Filter Controls */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <Button
                            variant="outline"
                            onClick={() => setIsDatePickerOpen(true)}
                            className="w-full justify-start text-left font-normal"
                        >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formatDateRange()}
                        </Button>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by employee or added by..."
                        />
                    </div>
                </div>
            </div>

            {/* Records Table */}
            <Card className="bg-white shadow rounded-lg mb-8">
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <CardTitle className="text-lg font-semibold text-gray-800">
                            Transaction History ({filteredRecords.length} records)
                        </CardTitle>
                        <Button
                            onClick={exportBartenderTipsRecordsToPDF}
                            variant="outline"
                            className="bg-yellow-400 hover:bg-yellow-500 text-black"
                        >
                            Export to PDF
                        </Button>
                    </div>
                </CardHeader>

                <CardContent>
                    <BartenderTipsTransactionTable
                        records={filteredRecords}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        entriesPerPage={entriesPerPage}
                        setEntriesPerPage={setEntriesPerPage}
                        onEdit={handleEditRecord}
                        onDelete={handleDeleteRecordClick}
                        currentUser={currentUser}
                        dateRange={dateRange}
                        employeeApiService={employeeManagementService}
                        employees={allemployees}
                    />
                </CardContent>
            </Card>

            {/* Employee Totals Section */}
            <Card className="bg-white shadow rounded-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-gray-800">
                        Overall Total Tips Received ({formatDateRange()})
                    </CardTitle>
                    <Button
                        onClick={() => fetchRecords(dateRange.start, dateRange.end)}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                    >
                        {isLoading ? "Refreshing..." : "Refresh"}
                    </Button>
                </CardHeader>
                <CardContent>
                    <EmployeeTotalsTable
                        employeeTotals={employeeTotals}
                        dateRange={dateRange}
                        employeeApiService={employeeManagementService}
                        employees={allemployees}
                    />
                </CardContent>
            </Card>

            {/* Period Summary */}
            {/* <Card className="bg-white shadow rounded-lg mt-8">
                <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-800">
                        Period Summary ({formatDateRange()})
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h3 className="text-md font-medium text-gray-700">Total Tips Received</h3>
                        <p className="text-2xl font-bold text-green-600">
                            ${formatCurrency(periodSummary.tipsReceived)}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-md font-medium text-gray-700">Total Owe to Toast</h3>
                        <p className="text-2xl font-bold text-blue-600">
                            ${formatCurrency(periodSummary.oweToToast)}
                        </p>
                    </div>
                </CardContent>
            </Card> */}

            {/* Modals */}
            <BartenderTipAddModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onAdd={handleAddRecord}
                employees={allemployees}
            />

            <BartenderTipEditModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                record={currentRecord}
                onUpdate={handleUpdateRecord}
                employees={allemployees}
                currentUser={currentUser}
            />

            <BartenderTipDeleteModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                record={currentRecord}
                onDelete={handleDeleteRecord}
            />

            {/* Date Range Picker */}
            <DateRangePicker
                isOpen={isDatePickerOpen}
                onClose={() => setIsDatePickerOpen(false)}
                onApply={(start, end) => {
                    setDateRange({
                        start: startOfDay(start),
                        end: endOfDay(end)
                    });
                    setIsDatePickerOpen(false);
                }}
                initialStartDate={dateRange.start}
                initialEndDate={dateRange.end}
            />
        </div>
    );
}